import { useState, useEffect, useCallback } from "react";

interface User {
  id: number;
  email: string;
}

interface StockItem {
  id: number;
  width: number;
  height: number;
  label: string;
  cost: number;
  material: string;
  is_remnant: number;
  created_at: string;
}

interface ProjectSummary {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface ProjectFull extends ProjectSummary {
  data: unknown;
}

async function api<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...opts,
    headers: { "Content-Type": "application/json", ...opts?.headers },
  });
  return res.json();
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const res = await api<{ user: User | null }>("/api/auth/me");
    setUser(res.user);
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const signup = useCallback(async (email: string, password: string) => {
    const res = await api<User & { error?: string }>("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    if ("error" in res && res.error) throw new Error(res.error);
    setUser(res);
    return res;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api<User & { error?: string }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    if ("error" in res && res.error) throw new Error(res.error);
    setUser(res);
    return res;
  }, []);

  const logout = useCallback(async () => {
    await api("/api/auth/logout", { method: "POST" });
    setUser(null);
  }, []);

  return { user, loading, signup, login, logout };
}

export function useStock(user: User | null) {
  const [items, setItems] = useState<StockItem[]>([]);

  const refresh = useCallback(async () => {
    if (!user) return;
    const data = await api<StockItem[]>("/api/stock");
    setItems(data);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addSheets = useCallback(
    async (
      sheets: Array<{
        width: number;
        height: number;
        label?: string;
        cost?: number;
        material?: string;
        is_remnant?: boolean;
      }>,
    ) => {
      await api("/api/stock", {
        method: "POST",
        body: JSON.stringify({ sheets }),
      });
      refresh();
    },
    [refresh],
  );

  const removeSheet = useCallback(
    async (id: number) => {
      await api(`/api/stock/${id}`, { method: "DELETE" });
      refresh();
    },
    [refresh],
  );

  return { items, addSheets, removeSheet, refresh };
}

export function useProjects(user: User | null) {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);

  const refresh = useCallback(async () => {
    if (!user) return;
    const data = await api<ProjectSummary[]>("/api/projects");
    setProjects(data);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveProject = useCallback(
    async (name: string, data: unknown, id?: number) => {
      if (id) {
        await api(`/api/projects/${id}`, {
          method: "PUT",
          body: JSON.stringify({ name, data }),
        });
      } else {
        const res = await api<{ id: number }>("/api/projects", {
          method: "POST",
          body: JSON.stringify({ name, data }),
        });
        refresh();
        return res.id;
      }
      refresh();
      return id;
    },
    [refresh],
  );

  const loadProject = useCallback(async (id: number) => {
    return api<ProjectFull>(`/api/projects/${id}`);
  }, []);

  const deleteProject = useCallback(
    async (id: number) => {
      await api(`/api/projects/${id}`, { method: "DELETE" });
      refresh();
    },
    [refresh],
  );

  return { projects, saveProject, loadProject, deleteProject, refresh };
}
